"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosAgentPricingPendingChangeRowSchema = exports.APosAgentPricingPendingChangeStatusSchema = exports.APosAgentPricingPendingChangeSourceSchema = exports.APosAgentPricingTenantOverrideRowSchema = exports.APosAgentPricingTenantOverrideApiSchema = exports.APosAgentPricingConfigRowSchema = exports.APosAgentPricingChangeHistoryEntrySchema = exports.APosAgentOperationDefinitionSchema = exports.APosAgentOperationExpectedBreakdownSchema = exports.APosAgentOperationCostModelSchema = exports.APosAgentApiRegistrySchema = exports.APosAgentApiRegistryCseSchema = exports.APosAgentApiRegistryVertexSchema = exports.APosAgentApiRegistryAnthropicSchema = exports.APosAgentApiCsePricingSchema = exports.APosAgentApiVertexPricingSchema = exports.APosAgentApiAnthropicPricingSchema = void 0;
const zod_1 = require("zod");
/**
 * AgenticPOS AI Agent Pricing — Entity Schemas
 *
 * Shape of rows stored in DynamoDB `APosAgentPricingTable`.
 *
 * Three row types share PK='PRICING':
 *   sk='CONFIG'                    → global singleton
 *   sk='TENANT#<tenantId>'         → per-tenant override
 *   sk='PENDING#<ISO>#<ulid>'      → daily-sync pending change queue
 *
 * See docs/superpowers/specs/2026-04-10-ai-product-assist-design.md §7.2
 */
// ---------- Per-API pricing inputs ----------
exports.APosAgentApiAnthropicPricingSchema = zod_1.z.object({
    input_per_million_usd: zod_1.z.number().nonnegative(),
    output_per_million_usd: zod_1.z.number().nonnegative(),
});
exports.APosAgentApiVertexPricingSchema = zod_1.z.object({
    gemini_flash_input_per_million_usd: zod_1.z.number().nonnegative(),
    gemini_flash_output_per_million_usd: zod_1.z.number().nonnegative(),
    gemini_flash_vision_per_image_usd: zod_1.z.number().nonnegative(),
});
exports.APosAgentApiCsePricingSchema = zod_1.z
    .object({
    per_call_usd: zod_1.z.number().nonnegative(),
    // Wave 1.6+: explicit grounding-per-call field. The google_cse
    // API slot is repurposed for Gemini's Google Search grounding tool
    // (the Custom Search JSON API is closed to new customers). When
    // grounding is in use, `per_call_usd` and `grounding_per_call_usd`
    // should be kept in sync — `grounding_per_call_usd` is the
    // authoritative name and is preferred by Wave 1.6+ callers.
    grounding_per_call_usd: zod_1.z.number().nonnegative().optional(),
})
    .passthrough();
// ---------- Global API registry entries ----------
const APosAgentApiRegistryBaseSchema = zod_1.z.object({
    label: zod_1.z.string(),
    burnRate: zod_1.z.number().nonnegative().max(50),
    minCreditsPerCall: zod_1.z.number().int().nonnegative().default(1),
    enabled: zod_1.z.boolean().default(true),
    // Populated by the daily pricing sync cron (Wave 1 Track D).
    // `lastSyncedAt` bumps on every successful sync pass (regardless of
    // whether a drift was detected).
    // `lastVerifiedAt` bumps only when the detected upstream rate
    // matches the current CONFIG value OR a super admin explicitly
    // applied/confirmed a pending change. See spec §9.4.
    lastSyncedAt: zod_1.z.string().optional(),
    lastVerifiedAt: zod_1.z.string().optional(),
});
exports.APosAgentApiRegistryAnthropicSchema = APosAgentApiRegistryBaseSchema.extend({
    pricing: exports.APosAgentApiAnthropicPricingSchema,
});
exports.APosAgentApiRegistryVertexSchema = APosAgentApiRegistryBaseSchema.extend({
    pricing: exports.APosAgentApiVertexPricingSchema,
});
exports.APosAgentApiRegistryCseSchema = APosAgentApiRegistryBaseSchema.extend({
    pricing: exports.APosAgentApiCsePricingSchema,
    // Legacy CSE-only field, optional now that the slot is repurposed
    // for Gemini grounding. The migration script removes it from the
    // CONFIG row but parsing must stay backward compatible for older
    // rows that still have it.
    freeTierCallsPerDay: zod_1.z.number().int().nonnegative().optional(),
});
exports.APosAgentApiRegistrySchema = zod_1.z.object({
    anthropic: exports.APosAgentApiRegistryAnthropicSchema,
    google_vertex: exports.APosAgentApiRegistryVertexSchema,
    google_cse: exports.APosAgentApiRegistryCseSchema,
});
// ---------- Operation registry ----------
exports.APosAgentOperationCostModelSchema = zod_1.z.enum([
    'dynamic',
    'composite',
]);
exports.APosAgentOperationExpectedBreakdownSchema = zod_1.z
    .object({
    google_cse: zod_1.z
        .object({
        calls: zod_1.z.number().int().nonnegative(),
        costUsd: zod_1.z.number().nonnegative(),
    })
        .optional(),
    google_vertex: zod_1.z
        .object({
        visionCalls: zod_1.z.number().int().nonnegative().optional(),
        descriptionCalls: zod_1.z.number().int().nonnegative().optional(),
        costUsd: zod_1.z.number().nonnegative(),
    })
        .optional(),
    anthropic: zod_1.z
        .object({
        costUsd: zod_1.z.number().nonnegative(),
    })
        .optional(),
})
    .passthrough();
exports.APosAgentOperationDefinitionSchema = zod_1.z.object({
    apisUsed: zod_1.z.array(zod_1.z.enum(['anthropic', 'google_vertex', 'google_cse'])),
    costModel: exports.APosAgentOperationCostModelSchema,
    reserveCap: zod_1.z.number().int().positive(),
    feature: zod_1.z.string().min(1),
    expectedBreakdown: exports.APosAgentOperationExpectedBreakdownSchema.optional(),
});
// ---------- Change history entry (shared by config + override rows) ----------
exports.APosAgentPricingChangeHistoryEntrySchema = zod_1.z.object({
    at: zod_1.z.string(),
    by: zod_1.z.string(),
    diff: zod_1.z.object({
        field: zod_1.z.string(),
        before: zod_1.z.unknown(),
        after: zod_1.z.unknown(),
    }),
    note: zod_1.z.string().optional(),
});
// ---------- Global CONFIG singleton ----------
exports.APosAgentPricingConfigRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('PRICING'),
    sk: zod_1.z.literal('CONFIG'),
    creditUnitPriceUsd: zod_1.z.number().positive().default(0.01),
    minPurchaseCredits: zod_1.z.number().int().positive().default(50),
    apis: exports.APosAgentApiRegistrySchema,
    operations: zod_1.z.record(zod_1.z.string(), exports.APosAgentOperationDefinitionSchema),
    version: zod_1.z.number().int().nonnegative().default(0),
    lastUpdatedAt: zod_1.z.string(),
    lastUpdatedBy: zod_1.z.string().optional(),
    changeHistory: zod_1.z
        .array(exports.APosAgentPricingChangeHistoryEntrySchema)
        .default([]),
});
// ---------- Tenant override row ----------
exports.APosAgentPricingTenantOverrideApiSchema = zod_1.z.object({
    burnRateOverride: zod_1.z.number().nonnegative().max(50).optional(),
    minCreditsPerCallOverride: zod_1.z
        .number()
        .int()
        .nonnegative()
        .optional(),
    free: zod_1.z.boolean().optional(),
});
exports.APosAgentPricingTenantOverrideRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('PRICING'),
    sk: zod_1.z.string(), // TENANT#<tenantId>
    tenantId: zod_1.z.string(),
    minPurchaseCredits: zod_1.z.number().int().positive().optional(),
    apis: zod_1.z
        .object({
        anthropic: exports.APosAgentPricingTenantOverrideApiSchema.optional(),
        google_vertex: exports.APosAgentPricingTenantOverrideApiSchema.optional(),
        google_cse: exports.APosAgentPricingTenantOverrideApiSchema.optional(),
    })
        .default({}),
    note: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string(),
    updatedBy: zod_1.z.string().optional(),
    auditLog: zod_1.z
        .array(exports.APosAgentPricingChangeHistoryEntrySchema)
        .default([]),
});
// ---------- Pending change queue row ----------
exports.APosAgentPricingPendingChangeSourceSchema = zod_1.z.enum([
    'google_billing_api',
    'anthropic_drift_check',
]);
exports.APosAgentPricingPendingChangeStatusSchema = zod_1.z.enum([
    'PENDING_REVIEW',
    'APPLIED',
    'DISMISSED',
]);
exports.APosAgentPricingPendingChangeRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('PRICING'),
    sk: zod_1.z.string(), // PENDING#<ISO>#<ulid>
    detectedAt: zod_1.z.string(),
    source: exports.APosAgentPricingPendingChangeSourceSchema,
    api: zod_1.z.enum(['anthropic', 'google_vertex', 'google_cse']),
    field: zod_1.z.string(), // dotted path
    currentValue: zod_1.z.number(),
    detectedValue: zod_1.z.number(),
    deltaPercent: zod_1.z.number(),
    status: exports.APosAgentPricingPendingChangeStatusSchema.default('PENDING_REVIEW'),
    reviewedAt: zod_1.z.string().optional(),
    reviewedBy: zod_1.z.string().optional(),
    actionNote: zod_1.z.string().optional(),
    metadata: zod_1.z
        .object({
        expectedUsd: zod_1.z.number().optional(),
        actualUsd: zod_1.z.number().optional(),
        drift_sample_date: zod_1.z.string().optional(),
    })
        .passthrough()
        .optional(),
});
//# sourceMappingURL=agent-pricing.schema.js.map